import { Injectable, NgZone } from '@angular/core';
import * as RecordRTC from 'recordrtc';
import * as moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { CommunicationInterfaceService } from '../communicationInterface/communication-interface.service';
import { AppdataService } from '../app-data/appdata.service';

interface RecordedAudioOutput {
  blob: Blob;
  title: string;
  fromFormNode: boolean;
  nodeIndex: number;
}

@Injectable({
  providedIn: 'root'
})
export class AudiorecordingService {
  private stream;
  private recorder;
  private interval;
  private startTime;
  private _recorded = new Subject<RecordedAudioOutput>();
  private _recordingTime = new Subject<string>();
  private _recordingFailed = new Subject<string>();
  private cancelRecording = new Subject<any>();
  private recordedAudioBlob: any;
  cancelRecordingObservable: Observable<any> = this.cancelRecording.asObservable();

  constructor(private commInterface: CommunicationInterfaceService, private appDataService: AppdataService) {}

  getRecordedBlob(): Observable<RecordedAudioOutput> {
    return this._recorded.asObservable();
  }

  getRecordedTime(): Observable<string> {
    return this._recordingTime.asObservable();
  }

  recordingFailed(): Observable<string> {
    return this._recordingFailed.asObservable();
  }

  startRecording() {
    try {
      if (this.recorder) {
        return;
      }
      this._recordingTime.next('00:00');
      this.appDataService
        .getUserMediaFn({ audio: true })
        .then((s) => {
          this.stream = s;
          this.record();
        })
        .catch((error) => {
          this.cancelRecording.next(true);
          this._recordingFailed.next('');
        });
    } catch (error) {
      console.error('%c Error Occured while starting audio record - ', 'color: red', error);
      this.commInterface.sendUIErrors('AudioRecordingService', 'startRecording', error);
    }
  }

  abortRecording() {
    this.stopMedia();
  }

  private record() {
    try {
      this.recorder = new MediaRecorder(this.stream);
      this.recorder.start();
      this.startTime = moment();
      this.interval = setInterval(() => {
        const currentTime = moment();
        const diffTime = moment.duration(currentTime.diff(this.startTime));
        const time = this.toString(diffTime.minutes()) + ':' + this.toString(diffTime.seconds());
        this._recordingTime.next(time);
      }, 1000);
    } catch (error) {
      console.error('%c Error Occured while initializing audio record - ', 'color: red', error);
      this.commInterface.sendUIErrors('AudioRecordingService', 'record', error);
    }
  }

  private toString(value) {
    try {
      let val = value;
      if (!value) {
        val = '00';
      }
      if (value < 10) {
        val = '0' + value;
      }
      return val;
    } catch (error) {
      console.error('%c Error Occured in toString method - ', 'color: red', error);
      this.commInterface.sendUIErrors('AudioRecordingService', 'toString', error);
    }
  }

  stopRecording(fromFormNode?: boolean, nodeIndex?: number) {
    try {
      this.recorder.addEventListener('dataavailable', (e) => {
        this.recordedAudioBlob = e.data;
        const audioBlob = new Blob([this.recordedAudioBlob], { type: 'audio/mpeg' });
        const mp3Name = encodeURIComponent('audio_' + new Date().getTime() + '.mp3');
        this._recorded.next({
          blob: audioBlob,
          title: mp3Name,
          fromFormNode: fromFormNode,
          nodeIndex: nodeIndex
        });
      });
      setTimeout(() => {
        this.recorder.stop();
        this.stopMedia();
      }, 1);
    } catch (error) {
      console.error('%c Error Occured while stopping audio record - ', 'color: red', error);
      this.commInterface.sendUIErrors('AudioRecordingService', 'stopRecording', error);
    }
  }

  private stopMedia() {
    try {
      if (this.recorder) {
        this.recorder = null;
        clearInterval(this.interval);
        this.startTime = null;
        if (this.stream) {
          this.stream.getAudioTracks().forEach((track) => track.stop());
          this.stream = null;
        }
      }
    } catch (error) {
      console.error('%c Error Occured while stopping audio tracks - ', 'color: red', error);
      this.commInterface.sendUIErrors('AudioRecordingService', 'stopMedia', error);
    }
  }
}
